import {TextProps} from '@visx/text'
import {first, isFunction, last, pipe, sortBy} from 'remeda'
import {Rnge} from '../range'
import {avgOf, max, min} from '../agg'
import {keyBy, mapWithPrev} from '../collection'
import {notNull} from '../type'
import {ComponentProps} from 'react'
import type {AnnotationLabel, AnnotationLineSubject} from '@visx/xychart'
const {abs, floor} = Math
import {QuantityForecastType} from '../forecast/forecastTypes'
import {type QuestionCfg} from '../forecast/question'
import {DistV1ForAgg} from './distribution.v1'
import {doSafe} from '../promise'
const {debug: consoleDebug} = console

export type DistributionLabel = {
  title?: string
  subTitle?: string
  x: number
  color?: string
  lineProps?: Partial<Omit<ComponentProps<typeof AnnotationLineSubject>, 'x1' | 'x2' | 'y1' | 'y2'>>
  titleProps?: Partial<TextProps>
  labelProps?: Partial<ComponentProps<typeof AnnotationLabel>>
  type?: 'xTop' | 'mainPoint' | 'xBottom' | 'point'
}

export type DistributionsSharedCfg = {
  bounds: [number, number]
  axisLabel?: string
  watermark?: string
  primaryColor?: string
  watermarkProps?: Partial<TextProps>
}

export namespace Dist {
  export const numTicks = 1000

  export type DistributionEmpty = {name?: string; labels?: DistributionLabel[]}
  export type DiscreteDistribution<T extends Point = Point> = {
    name?: string
    pts: T[]
    closestPt: (x: number) => Point | undefined
    adjustedPt: (x: number) => Point
    /** returns X given a percentile */
    percentileX: (pct: number, options?: {debugName?: string}) => number | undefined
    /** returns the x value at the 50th percentile */
    percentile50: number | undefined
    /** finds a point representing that accumulated probability. We adjust x to match pAcc correctly */
    labels?: DistributionLabel[]
  } & WithTails

  /** function that returns the probability at a given x */
  export type Pdf = {
    type: QuantityForecastType
    getP: (x: number) => number | undefined
    /** the effective bounds (either hard or practical) of this forecast, outside this range is negligible or empty */
  } & WithBounds

  export type WithBounds = {bounds: [number, number]}
  export type Tail = {pct: number; mode: 'tail' | 'bound'} // tail at the location specified by bounds of the distribution
  export type WithTails = {left?: Tail; right?: Tail}

  /** a pdf plus information required for aggregation */
  export type PdfForAgg = Pdf &
    WithTails & {
      aggVersion?: QuestionCfg['aggVersion']
    }

  export type Distribution = DiscreteDistribution | DistributionEmpty
  export const isValid = (d: Distribution): d is DiscreteDistribution => 'pts' in d

  export const createAggRange = (ranges: [number, number][], n: number, pad: number = 0) => {
    const largestFirst = sortBy(ranges, [r => Rnge.width(r), 'desc'])

    const bounds = [min(ranges.map(r => r[0]))!, max(ranges.map(r => r[1]))!] as [number, number]
    const [left, right] = bounds

    let range = Rnge.create([left - pad, right + pad], n) // create ticks for the full range and then in order of the largest range first, add the ticks for the smaller ranges
    largestFirst.forEach(([start, end]) => {
      const rangeTicks = Rnge.create([start, end], n)
      range = [...range.filter(t => t < start), ...rangeTicks, ...range.filter(t => t > end)]
    })

    return {range, bounds}
  }

  /** creates a range as per normal unless pad is provided.
   * This is the number of points you want outside the range given
   * This is useful for discrete pdf/cdf logic which may need surrounding points
   *  */
  export const createRange = (range: [number, number], n: number, pad: number = 0) => {
    const padWidth = (Rnge.width(range) / (n - pad * 2)) * pad
    return Rnge.create([range[0] - padWidth, range[1] + padWidth], n)
  }

  export const agg = (
    pdfs: PdfForAgg[],
    options?: {
      displayBounds?: [number, number]
      hardBounds?: [number?, number?]
      title?: string
      ticks?: number
      aggVersion?: QuestionCfg['aggVersion']
      debugName?: string
    }
  ): DiscreteDistribution => {
    if (options?.aggVersion == '1') {
      return DistV1ForAgg.agg(
        pdfs.map(pdf => {
          // v1 actually discretized pdf before agg with a different function. But the sole difference is the padded range
          const range =
            pdf.type == 'logistic' ? createRange(pdf.bounds, options?.ticks ?? 1000, 1).map(x => ({x})) : undefined
          return pdfToDiscrete(pdf, {range})
        }),
        options
      )
    }

    const ticks = options?.ticks ?? numTicks
    const {range: aggRange, bounds: aggBounds} = createAggRange(
      pdfs.map(({bounds}) => bounds),
      ticks
    )

    const range = aggRange.map((x, i) => ({x, id: i.toString()}))

    // create pdfs with the agg with the same set of points/ticks
    const pdfsDiscrete = pdfs.map(pdf => keyBy(pdfToDiscretePoints(pdf, {range}), p => p.id))

    // convert the pdf's to a and avg of each as a cdf
    const cdfPoints = range
      .map(({x, id}) => ({
        x,
        pAcc: avgOf(
          pdfsDiscrete.map(d => d[id]!),
          p => p.pAcc
        )
      }))
      .filter(p => p.pAcc != null) as {x: number; pAcc: number}[]

    //  covert back to a cdf and filter out the points outside the display bounds
    // use tailBounds to aggregate into the tails properly. Needs to handle points happening to be outside the bounds and also explicity at the bounds
    const displayBounds = options?.displayBounds ?? aggBounds
    const tailBounds = [
      doSafe(options?.hardBounds?.[0], min => Math.max(displayBounds[0], min)) ?? displayBounds[0],
      doSafe(options?.hardBounds?.[1], max => Math.min(displayBounds[1], max)) ?? displayBounds[1]
    ] as [number, number]
    const {pts, left, right} = pipe(fromCdToPdPoints(cdfPoints), pts => {
      const ptsDisplayed = pts.filter(p => p.x > tailBounds[0] && p.x < tailBounds[1])
      const getTail = (side: 'left' | 'right') => Dist.getTail({side, pts, ptsDisplayed, bounds: tailBounds, pdfs})
      return {pts: ptsDisplayed, left: getTail('left'), right: getTail('right')}
    })

    return withDiscreteFns({pts, left, right, bounds: tailBounds})
  }

  /** calculates the pct and mode of the tail of the distribution */
  export const getTail = ({
    side,
    pts,
    ptsDisplayed,
    bounds,
    pdfs
  }: {
    side: 'left' | 'right'
    pts: Point[]
    ptsDisplayed: Point[]
    bounds: [number, number]
    pdfs: Pick<PdfForAgg, 'left' | 'right'>[]
  }) => {
    // const ptsInTail = pts.filter(p => (side == 'left' ? p.x < bounds[0] : p.x > bounds[1]))
    const pct =
      side === 'left' ?
        (() => {
          let pct = first(pts)?.pAcc ?? 0
          const rightPt = last(pts)
          // special case for when the entire curve is outside the bounds to the left
          if (pts.length >= 2 && ptsDisplayed.length == 0 && rightPt && rightPt.x < bounds[0]) return rightPt.pAcc
          return pct
        })()
      : 1 - (last(ptsDisplayed)?.pAcc ?? 0)

    const allModes = pdfs.map(pdf => pdf[side]?.mode).filter(notNull)
    const mode = allModes.length > 0 && allModes.every(m => m == 'bound') ? ('bound' as const) : ('tail' as const)

    return {pct, mode, ptsDisplayed}
  }

  export const distAtPoints = (dist: DiscreteDistribution, xs: number[]) =>
    fromCdToPdPoints(xs.map(x => dist.adjustedPt(x)).filter(notNull))

  /** Add common distribution functions to an array of points */
  export const withDiscreteFns = <T extends Point>(
    {pts, left, right, bounds}: {pts: T[]} & WithTails & WithBounds,
    options?: {debugName?: string}
  ): DiscreteDistribution<T> => {
    const closestPt = (x: number) => findClosestPt(pts, pt => pt.x, x)
    const adjustedPt = (x: number) =>
      findAdjustedPt(pts, 'x', x) ?? {x, p: 0, pAcc: pts.length > 0 && x > last(pts)!.x ? last(pts)!.pAcc : 0}
    const percentileX = (pct: number, options?: {debugName?: string}) => {
      if (options?.debugName) consoleDebug('percentileX A', options.debugName, {left, right, pct})
      if (left && pct <= left.pct) return bounds[0] // issue is that this could be display not hard...
      if (right && pct >= 1 - right.pct) return bounds[1]
      const pt = findAdjustedPt(pts, 'pAcc', pct, options)
      if (options?.debugName) consoleDebug('percentileX B', options.debugName, {pt})
      if (pct > 1) throw new Error('percentileX: pct must be between 0 and 1')
      if (!pt) return undefined // if it's within the tail% then it is missing
      //  adjust with width/2 for symmetry. see approach d in https://www.figma.com/file/U56xNQ3THrSlMZtamccx0C/Swift-Portal?type=design&node-id=618%3A10779&t=ZSBlGY94C91bEWlr-1
      const res = pt.x + pt.xWidth / 2
      if (options?.debugName) consoleDebug('percentileX C', options.debugName, {res, pt, pct, pts})
      return res
    }
    const percentile50 = percentileX(0.5, options)
    return {pts, closestPt, adjustedPt, percentileX, percentile50, left, right}
  }

  export const mapCoords = (fromX: number, from: [number, number], to: [number, number]) => {
    const portion = (fromX - from[0]) / (from[1] - from[0])
    return to[0] + portion * (to[1] - to[0])
  }

  // #region Standard Distribution math

  export interface Point {
    /** the probability (any scale you want) at that point */
    p: number
    /** the accumulated probability from left to right.
     * see Approach D in this figma for a diagram https://www.figma.com/file/U56xNQ3THrSlMZtamccx0C/Swift-Portal?type=design&node-id=618%3A10779&t=ZSBlGY94C91bEWlr-1
     */
    pAcc: number
    x: number
  }

  type AdjustedPoint = (Point & {xWidth: number}) | undefined

  /** finds the adjacent items and linearly extrapolates what this point should be */
  const findAdjustedPt = (
    pts: Point[],
    by: 'pAcc' | 'x',
    value: number,
    options?: {debugName?: string}
  ): AdjustedPoint => {
    const items = adjacentItems(pts, p => p[by], value, options)
    // if (options?.debugName) consoleDebug('findAdjustedPt', options?.debugName, items)
    if (!items) return undefined

    const [below, above] = items

    // If value matches either point exactly, return that point
    if (below[by] === value) return {...below, xWidth: 0}
    if (above[by] === value) return {...above, xWidth: 0}

    // Otherwise interpolate between points
    const vWidth = above[by] - below[by]
    const xWidth = above.x - below.x
    if (vWidth == 0) return {...above, xWidth}

    const pctOfDv = (value - below[by]) / vWidth
    const otherV = by == 'pAcc' ? 'x' : 'pAcc'
    const otherVal = below[otherV] + (above[otherV] - below[otherV]) * pctOfDv
    const p = below.p + (above.p - below.p) * pctOfDv

    return {p, [by]: value, [otherV]: otherVal, xWidth} as any as AdjustedPoint
  }

  /** finds the first item in a sorted array that has `by` >= value */
  export const findClosestPt = <T>(pts: T[], by: ((n: T) => number) | keyof T, value: number) => {
    const f = isFunction(by) ? by : (n: T) => n[by] as number
    const i = adjacentItems(pts, f, value)
    if (!i) {
      return undefined
    }
    const [p1, p2] = i
    return abs(value - f(p1)) < abs(value - f(p2)) ? p1 : p2
  }

  export const abovePt = <T>(pts: T[], by: ((n: T) => number) | keyof T, value: number) => {
    const f = isFunction(by) ? by : (n: T) => n[by] as number
    return adjacentItems(pts, f, value)?.[1]
  }

  /** finds the items that surround the given value */
  const adjacentItems = <T>(
    pts: T[],
    by: (n: T) => number,
    value: number,
    options?: {debugName?: string}
  ): [T, T] | undefined => {
    let l = 0
    let r = pts.length - 1
    while (l <= r) {
      const mid = floor((l + r) / 2)
      const above = pts[mid]!
      const below = mid > 0 ? pts[mid - 1] : undefined
      const aboveValue = by(above)
      if (aboveValue == value || (aboveValue >= value && below && by(below) < value)) {
        // return the by as given, and linearly adjust the other points
        return aboveValue == value ? [above, above] : [below ?? above, above]
      }
      if (aboveValue > value) r = mid - 1
      else l = mid + 1
    }
    // if (options?.debugName) consoleDebug('adjacentItems', options.debugName, {items, value, by})
    return undefined
  }

  export const pdfToDiscrete = <T extends {x: number}>(
    pdf: PdfForAgg & WithBounds & WithTails,
    options?: ToDiscreteOptions<T> & {debugName?: string}
  ) => {
    const pts = pdfToDiscretePoints(pdf, options)
    const {bounds, left, right} = pdf
    return {
      ...withDiscreteFns({pts, bounds, left, right}, {debugName: options?.debugName}),
      ...pdf
    }
  }

  export type ToDiscreteOptions<
    T extends {
      x: number
    } = {x: number}
  > = {
    range?: T[]
    ticks?: number
    debugName?: string
  }

  /** derives pAcc from p given a pdf function and a range of x values
   * p is the probability at the point, and pAcc is the accumulated probability (0-1) including this one.
   *  works with non-uniform x spacing */
  export const pdfToDiscretePoints = <T extends {x: number}>(pdf: PdfForAgg, options?: ToDiscreteOptions<T>) => {
    let pAcc = 0
    const range = options?.range ?? createRange(pdf.bounds, options?.ticks ?? numTicks).map(x => ({x}))
    const basicAcc = mapWithPrev(range, (pt, {prev, next}) => {
      if (!next && !prev) console.warn('withCdFromPd item with to prev or next')
      const dx =
        next ? next.x - pt.x
        : prev ? pt.x - prev!.x
        : 0 // calculate the width, for the last point assume it is the same with as previous as a convention to be able to round-trip cpd->cd->pd
      const p = pdf.getP(pt.x) ?? 0
      pAcc += p * dx
      return {...(pt as T), pAcc, p}
    })

    const leftPct = pdf.left?.pct ?? 0
    const normalize = last(basicAcc)!.pAcc > 0 ? (1 - leftPct - (pdf.right?.pct ?? 0)) / last(basicAcc)!.pAcc : 1
    let res = basicAcc.map(pt => ({
      ...pt,
      pAcc: leftPct + pt.pAcc * normalize
    }))

    return res
  }

  /** derives p from pAcc. Usefull if you have manipulated pAcc (e.g. aggregating) and then need to display p
   * Ignores tails
   */
  export const fromCdToPdPoints = (cd: Pick<Point, 'pAcc' | 'x'>[], options?: {disableNormalize?: boolean}) =>
    pipe(
      mapWithPrev(cd, (pt, {prev, next}) => {
        const dx = next ? next.x - pt.x : pt.x - prev!.x
        const p = dx == 0 || prev == null ? 0 : (pt.pAcc - (prev?.pAcc ?? 0)) / dx
        return {...pt, p}
      }),
      options?.disableNormalize ? p => p : normalizeP
    )

  /** make the maximum P to be 1 */
  const normalizeP = <P extends {p: number}>(pd: P[]) => {
    if (pd.length < 2) return pd
    const maxP = max(pd.map(p => p.p))
    if (maxP == 0) return pd
    return pd.map(p => ({...p, p: p.p / maxP}))
  }

  // #endregion
}
